import { userManagementApi } from './userManagementService';

// Tipuri pentru audit log
export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  action: string;
  resource: string;
  resourceId?: string;
  resourceType: 'user' | 'clinic' | 'appointment' | 'payment' | 'patient' | 'inventory' | 'settings' | 'system';
  details: {
    before?: any;
    after?: any;
    changes?: {
      field: string;
      oldValue: any;
      newValue: any;
    }[];
  };
  ipAddress: string;
  userAgent: string;
  sessionId: string;
  timestamp: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'authentication' | 'authorization' | 'data_access' | 'data_modification' | 'system' | 'security' | 'business';
  outcome: 'success' | 'failure' | 'error';
  metadata?: {
    clinicId?: string;
    requestId?: string;
    duration?: number;
    [key: string]: any;
  };
}

// Tipuri pentru rapoarte audit
export interface AuditReport {
  id: string;
  name: string;
  description: string;
  type: 'security' | 'compliance' | 'activity' | 'performance' | 'custom';
  filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    clinicId?: string;
    action?: string;
    resourceType?: string;
    severity?: string;
    category?: string;
    outcome?: string;
  };
  schedule?: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    time: string;
    recipients: string[];
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastGenerated?: string;
  generatedBy?: string;
}

// Tipuri pentru statistici audit
export interface AuditStats {
  total: number;
  byCategory: {
    [key: string]: number;
  };
  bySeverity: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  byOutcome: {
    success: number;
    failure: number;
    error: number;
  };
  byResourceType: {
    [key: string]: number;
  };
  topUsers: {
    userId: string;
    userName: string;
    count: number;
  }[];
  topActions: {
    action: string;
    count: number;
  }[];
  trends: {
    date: string;
    count: number;
    category: string;
  }[];
  securityEvents: {
    failedLogins: number;
    privilegeEscalations: number;
    dataBreaches: number;
    suspiciousActivity: number;
  };
}

// Tipuri pentru filtre audit
export interface AuditFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  clinicId?: string;
  action?: string;
  resourceType?: string;
  severity?: string;
  category?: string;
  outcome?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Tipuri pentru reguli de alerta audit
export interface AuditAlertRule {
  id: string;
  name: string;
  description: string;
  conditions: {
    category?: string;
    severity?: string;
    action?: string;
    resourceType?: string;
    threshold: number;
    timeWindow: number; // in minute
  };
  actions: {
    type: 'email' | 'sms' | 'webhook' | 'slack' | 'teams';
    config: any;
  }[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru compliance
export interface ComplianceCheck {
  id: string;
  name: string;
  description: string;
  standard: 'GDPR' | 'HIPAA' | 'SOX' | 'ISO27001' | 'PCI-DSS' | 'custom';
  category: 'data_protection' | 'access_control' | 'audit_trail' | 'encryption' | 'retention' | 'other';
  requirements: {
    id: string;
    description: string;
    isRequired: boolean;
    weight: number;
  }[];
  status: 'compliant' | 'non_compliant' | 'partial' | 'not_assessed';
  score: number;
  lastChecked: string;
  nextCheck: string;
  findings: {
    id: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    description: string;
    recommendation: string;
    status: 'open' | 'in_progress' | 'resolved' | 'accepted_risk';
  }[];
  evidence: {
    id: string;
    type: 'document' | 'screenshot' | 'log' | 'test_result';
    description: string;
    url?: string;
    uploadedAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

class AuditService {
  private baseUrl = '/master-admin/audit';

  // Audit logs
  async getAuditLogs(filters?: AuditFilters): Promise<{ logs: AuditLog[]; total: number; page: number; limit: number; }> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.resourceType) params.append('resourceType', filters.resourceType);
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.outcome) params.append('outcome', filters.outcome);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await userManagementApi.get(`${this.baseUrl}/logs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  async getAuditLogById(id: string): Promise<AuditLog> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/logs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  }

  // Statistici audit
  async getAuditStats(filters?: { startDate?: string; endDate?: string; clinicId?: string; }): Promise<AuditStats> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}/stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit stats:', error);
      throw error;
    }
  }

  // Rapoarte audit
  async getAuditReports(): Promise<{ reports: AuditReport[]; total: number; }> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/reports`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit reports:', error);
      throw error;
    }
  }

  async getAuditReportById(id: string): Promise<AuditReport> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/reports/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit report:', error);
      throw error;
    }
  }

  async createAuditReport(report: Omit<AuditReport, 'id' | 'createdAt' | 'updatedAt'>): Promise<AuditReport> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/reports`, report);
      return response.data;
    } catch (error) {
      console.error('Error creating audit report:', error);
      throw error;
    }
  }

  async updateAuditReport(id: string, report: Partial<AuditReport>): Promise<AuditReport> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/reports/${id}`, report);
      return response.data;
    } catch (error) {
      console.error('Error updating audit report:', error);
      throw error;
    }
  }

  async deleteAuditReport(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/reports/${id}`);
    } catch (error) {
      console.error('Error deleting audit report:', error);
      throw error;
    }
  }

  async generateAuditReport(id: string): Promise<{ success: boolean; message: string; reportUrl?: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/reports/${id}/generate`);
      return response.data;
    } catch (error) {
      console.error('Error generating audit report:', error);
      throw error;
    }
  }

  // Reguli de alerta audit
  async getAuditAlertRules(): Promise<{ rules: AuditAlertRule[]; total: number; }> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/alert-rules`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit alert rules:', error);
      throw error;
    }
  }

  async getAuditAlertRuleById(id: string): Promise<AuditAlertRule> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/alert-rules/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit alert rule:', error);
      throw error;
    }
  }

  async createAuditAlertRule(rule: Omit<AuditAlertRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<AuditAlertRule> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/alert-rules`, rule);
      return response.data;
    } catch (error) {
      console.error('Error creating audit alert rule:', error);
      throw error;
    }
  }

  async updateAuditAlertRule(id: string, rule: Partial<AuditAlertRule>): Promise<AuditAlertRule> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/alert-rules/${id}`, rule);
      return response.data;
    } catch (error) {
      console.error('Error updating audit alert rule:', error);
      throw error;
    }
  }

  async deleteAuditAlertRule(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/alert-rules/${id}`);
    } catch (error) {
      console.error('Error deleting audit alert rule:', error);
      throw error;
    }
  }

  async testAuditAlertRule(id: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/alert-rules/${id}/test`);
      return response.data;
    } catch (error) {
      console.error('Error testing audit alert rule:', error);
      throw error;
    }
  }

  // Compliance
  async getComplianceChecks(): Promise<{ checks: ComplianceCheck[]; total: number; }> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/compliance`);
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance checks:', error);
      throw error;
    }
  }

  async getComplianceCheckById(id: string): Promise<ComplianceCheck> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/compliance/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance check:', error);
      throw error;
    }
  }

  async createComplianceCheck(check: Omit<ComplianceCheck, 'id' | 'createdAt' | 'updatedAt'>): Promise<ComplianceCheck> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/compliance`, check);
      return response.data;
    } catch (error) {
      console.error('Error creating compliance check:', error);
      throw error;
    }
  }

  async updateComplianceCheck(id: string, check: Partial<ComplianceCheck>): Promise<ComplianceCheck> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/compliance/${id}`, check);
      return response.data;
    } catch (error) {
      console.error('Error updating compliance check:', error);
      throw error;
    }
  }

  async deleteComplianceCheck(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/compliance/${id}`);
    } catch (error) {
      console.error('Error deleting compliance check:', error);
      throw error;
    }
  }

  async runComplianceCheck(id: string): Promise<{ success: boolean; message: string; score?: number; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/compliance/${id}/run`);
      return response.data;
    } catch (error) {
      console.error('Error running compliance check:', error);
      throw error;
    }
  }

  // Export audit
  async exportAuditLogs(filters?: AuditFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.resourceType) params.append('resourceType', filters.resourceType);
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.outcome) params.append('outcome', filters.outcome);
      if (filters?.search) params.append('search', filters.search);

      const response = await userManagementApi.get(`${this.baseUrl}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  }

  // Real-time monitoring
  async subscribeToAuditLogs(_callback: (log: AuditLog) => void): Promise<() => void> {
    // Implementare WebSocket sau Server-Sent Events
    // Pentru moment, returnam o functie mock
    return () => {};
  }
}

export const auditService = new AuditService();
